import { apiClient } from './api';
import {
  RagTagUploadRequest,
  RagTagUploadResponse,
  RagTagContent,
  RagTagQueryRequest,
  RagTagQueryResponse,
  RagTagSummarizeRequest,
  RagTagSummarizeResponse,
  RagTagFAQRequest,
  RagTagFAQResponse,
  RagTagSession,
  QueryHistory,
  SummaryHistory,
  FAQHistory,
} from '../models/ragTag';

const SESSION_STORAGE_KEY = 'ragTagSessions';

export class RagTagService {
  // API calls
  static async upload(request: RagTagUploadRequest): Promise<RagTagUploadResponse> {
    const response = await apiClient.post('/api/ragtag/upload', request);
    return response.data;
  }

  static async getContent(contentId: string): Promise<RagTagContent> {
    const response = await apiClient.get(`/api/ragtag/${contentId}`);
    return response.data;
  }

  static async query(request: RagTagQueryRequest): Promise<RagTagQueryResponse> {
    const response = await apiClient.post('/api/ragtag/query', request);
    return response.data;
  }

  static async summarize(request: RagTagSummarizeRequest): Promise<RagTagSummarizeResponse> {
    const response = await apiClient.post('/api/ragtag/summarize', request);
    return response.data;
  }

  static async generateFAQ(request: RagTagFAQRequest): Promise<RagTagFAQResponse> {
    const response = await apiClient.post('/api/ragtag/generate/faq', request);
    return response.data;
  }

  // Session management
  static createSession(): RagTagSession {
    const session: RagTagSession = {
      id: `ragtag-${Date.now()}`,
      contents: [],
      queries: [],
      summaries: [],
      faqs: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.saveSession(session);
    return session;
  }

  static getSession(sessionId: string): RagTagSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === sessionId) || null;
  }

  static getAllSessions(): RagTagSession[] {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static saveSession(session: RagTagSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }

    session.updatedAt = new Date().toISOString();
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
  }

  static deleteSession(sessionId: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filtered));
  }

  static addContent(sessionId: string, content: RagTagContent): void {
    const session = this.getSession(sessionId);
    if (session) {
      // Check if content already exists
      const existingIndex = session.contents.findIndex(c => c.contentId === content.contentId);
      if (existingIndex >= 0) {
        session.contents[existingIndex] = content;
      } else {
        session.contents.push(content);
      }
      this.saveSession(session);
    }
  }

  static removeContent(sessionId: string, contentId: string): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.contents = session.contents.filter(c => c.contentId !== contentId);
      this.saveSession(session);
    }
  }

  static addQuery(sessionId: string, query: string, response: RagTagQueryResponse): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.queries.push({
        query,
        answer: response.answer,
        relevantChunks: response.relevantChunks,
        timestamp: new Date().toISOString(),
      });
      this.saveSession(session);
    }
  }

  static addSummary(sessionId: string, contentIds: string[], type: 'brief' | 'detailed' | 'executive', summary: string): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.summaries.push({
        contentIds,
        type,
        summary,
        timestamp: new Date().toISOString(),
      });
      this.saveSession(session);
    }
  }

  static addFAQs(sessionId: string, contentIds: string[], response: RagTagFAQResponse): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.faqs.push({
        contentIds,
        faqs: response.faqs,
        timestamp: new Date().toISOString(),
      });
      this.saveSession(session);
    }
  }

  static exportSession(session: RagTagSession): string {
    let content = `# RAG Tag Knowledge Base Session\n\n`;
    content += `**Created:** ${new Date(session.createdAt).toLocaleString()}\n`;
    content += `**Updated:** ${new Date(session.updatedAt).toLocaleString()}\n\n`;
    
    if (session.contents.length > 0) {
      content += `## Uploaded Contents\n\n`;
      session.contents.forEach((c, index) => {
        content += `### ${index + 1}. ${c.metadata?.title || c.contentId}\n`;
        if (c.metadata?.tags?.length) {
          content += `**Tags:** ${c.metadata.tags.join(', ')}\n`;
        }
        if (c.metadata?.source) {
          content += `**Source:** ${c.metadata.source}\n`;
        }
        content += `**Type:** ${c.contentType}\n`;
        content += `**Chunks:** ${c.metadata?.chunks || 'Unknown'}\n\n`;
      });
    }

    if (session.queries.length > 0) {
      content += `## Queries\n\n`;
      session.queries.forEach((q, index) => {
        content += `### Query ${index + 1} (${new Date(q.timestamp).toLocaleString()})\n\n`;
        content += `**Question:** ${q.query}\n\n`;
        content += `**Answer:** ${q.answer}\n\n`;
        if (q.relevantChunks.length > 0) {
          content += `**Relevant Sources:**\n`;
          q.relevantChunks.forEach((chunk, i) => {
            content += `${i + 1}. ${chunk.metadata?.title || chunk.contentId} (Score: ${chunk.score.toFixed(3)})\n`;
          });
          content += '\n';
        }
      });
    }

    if (session.summaries.length > 0) {
      content += `## Summaries\n\n`;
      session.summaries.forEach((s, index) => {
        content += `### Summary ${index + 1} - ${s.type} (${new Date(s.timestamp).toLocaleString()})\n\n`;
        content += `**Documents:** ${s.contentIds.join(', ')}\n\n`;
        content += `${s.summary}\n\n`;
      });
    }

    if (session.faqs.length > 0) {
      content += `## Frequently Asked Questions\n\n`;
      session.faqs.forEach((faqSet, setIndex) => {
        content += `### FAQ Set ${setIndex + 1} (${new Date(faqSet.timestamp).toLocaleString()})\n`;
        content += `**Documents:** ${faqSet.contentIds.join(', ')}\n\n`;
        faqSet.faqs.forEach((faq, index) => {
          content += `**Q${index + 1}:** ${faq.question}\n`;
          content += `**A:** ${faq.answer}\n\n`;
        });
      });
    }

    return content;
  }
}